import apiService from '../apiService';

// ========== 題庫管理 (Form Topics) ==========

export const getFormTopicTypes = async () => {
    return apiService.GET('/api/admin/form-topics/types');
};

export const getFormTopics = async (params = {}) => {
    return apiService.GET('/api/admin/form-topics', params);
};

export const getFormTopic = async (id) => {
    return apiService.GET(`/api/admin/form-topics/${id}`);
};

export const createFormTopic = async (data) => {
    return apiService.POST('/api/admin/form-topics', data);
};

export const updateFormTopic = async (id, data) => {
    return apiService.PUT(`/api/admin/form-topics/${id}`, data);
};

export const deleteFormTopic = async (id) => {
    return apiService.DELETE(`/api/admin/form-topics/${id}`);
};

// ========== 活動管理 (Form Activities) ==========

export const getFormActivities = async (params = {}) => {
    return apiService.GET('/api/admin/form-activities', params);
};

export const getFormActivity = async (id) => {
    return apiService.GET(`/api/admin/form-activities/${id}`);
};

export const createFormActivity = async (data) => {
    return apiService.POST('/api/admin/form-activities', data);
};

export const updateFormActivity = async (id, data) => {
    return apiService.PUT(`/api/admin/form-activities/${id}`, data);
};

export const toggleFormActivity = async (id) => {
    return apiService.PATCH(`/api/admin/form-activities/${id}/toggle`);
};

export const deleteFormActivity = async (id) => {
    return apiService.DELETE(`/api/admin/form-activities/${id}`);
};

// ========== 場次管理 (Form Sessions) ==========

export const getFormSessions = async (activityId, params = {}) => {
    return apiService.GET(
        `/api/admin/form-activities/${activityId}/sessions`,
        params
    );
};

export const createFormSession = async (activityId, data) => {
    return apiService.POST(
        `/api/admin/form-activities/${activityId}/sessions`,
        data
    );
};

export const updateFormSession = async (activityId, sessionId, data) => {
    return apiService.PUT(
        `/api/admin/form-activities/${activityId}/sessions/${sessionId}`,
        data
    );
};

export const toggleFormSession = async (activityId, sessionId) => {
    return apiService.PATCH(
        `/api/admin/form-activities/${activityId}/sessions/${sessionId}/toggle`
    );
};

export const deleteFormSession = async (activityId, sessionId) => {
    return apiService.DELETE(
        `/api/admin/form-activities/${activityId}/sessions/${sessionId}`
    );
};

// ========== 表單管理 (Forms) ==========

export const getForms = async (params = {}) => {
    return apiService.GET('/api/admin/forms', params);
};

export const getForm = async (id) => {
    return apiService.GET(`/api/admin/forms/${id}`);
};

export const createForm = async (data) => {
    return apiService.POST('/api/admin/forms', data);
};

export const updateForm = async (id, data) => {
    return apiService.PUT(`/api/admin/forms/${id}`, data);
};

export const copyForm = async (id) => {
    return apiService.POST(`/api/admin/forms/${id}/copy`);
};

export const toggleForm = async (id) => {
    return apiService.PATCH(`/api/admin/forms/${id}/toggle`);
};

// ========== 提交記錄 (Submissions) ==========

export const getFormSubmissions = async (formId, params = {}) => {
    return apiService.GET(`/api/admin/forms/${formId}/submissions`, params);
};

export const exportFormSubmissions = async (formId, params = {}) => {
    return apiService.GET(`/api/admin/forms/${formId}/submissions/export`, params, {
        responseType: 'blob'
    });
};

export const getFormStats = async (formId) => {
    return apiService.GET(`/api/admin/forms/${formId}/stats`);
};

export const getFormAnswerStats = async (formId, params = {}) => {
    return apiService.GET(`/api/admin/forms/${formId}/answer-stats`, params);
};

export const exportFormAnswerStats = async (formId, params = {}) => {
    return apiService.GET(
        `/api/admin/forms/${formId}/answer-stats/export`,
        params,
        { responseType: 'blob' }
    );
};

export const getFormSubmission = async (submissionId) => {
    return apiService.GET(`/api/admin/form-submissions/${submissionId}`);
};

export const getFormSubmissionLogs = async (submissionId) => {
    return apiService.GET(`/api/admin/form-submissions/${submissionId}/logs`);
};

export const updateFormSubmissionCompletion = async (submissionId, data) => {
    return apiService.PATCH(
        `/api/admin/form-submissions/${submissionId}/completion`,
        data
    );
};

export const approveEnrollmentReview = async (submissionId, data = {}) => {
    return apiService.POST(`/api/admin/form-submissions/${submissionId}/enrollment-review/approve`, data);
};

export const rejectEnrollmentReview = async (submissionId, data = {}) => {
    return apiService.POST(`/api/admin/form-submissions/${submissionId}/enrollment-review/reject`, data);
};

// ========== 配額類別字典管理 (Form Quota Categories) ==========

/**
 * 取得所有配額類別（供下拉選單使用）
 * @returns {Promise} API 響應 - data 為類別陣列
 */
export const getFormQuotaCategories = async () => {
    const res = await apiService.GET('/api/admin/form-quota-categories');
    // 正規化 data 為陣列（相容舊版 Resource 再包一層 data）
    if (res && res.data != null && !Array.isArray(res.data)) {
        if (Array.isArray(res.data.data)) {
            return { ...res, data: res.data.data };
        }
    }
    return res;
};

/** 從 getFormQuotaCategories 回應建立 id→name 對照 */
export const buildQuotaCategoryNameMap = (list = []) => {
    const map = {};
    (Array.isArray(list) ? list : []).forEach((c) => {
        if (c == null || c.id == null) return;
        const name = c.name || c.label || '';
        map[c.id] = name;
        map[String(c.id)] = name;
    });
    return map;
};

/**
 * 新增配額類別
 * @param {{ name: string }} data
 * @returns {Promise} API 響應
 */
export const createFormQuotaCategory = async (data) => {
    return apiService.POST('/api/admin/form-quota-categories', data);
};

/**
 * 啟用/停用配額類別
 * @param {number} id - 類別 ID
 * @returns {Promise} API 響應
 */
export const toggleFormQuotaCategory = async (id) => {
    return apiService.PATCH(`/api/admin/form-quota-categories/${id}/toggle`);
};

// ========== 報名名單管理 (Registrations) ==========

/**
 * 取得報名名單列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getRegistrations = async (params = {}) => {
    return apiService.GET('/api/admin/registrations', params);
};

/**
 * 匯出報名名單 Excel
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應 (Blob)
 */
export const exportRegistrations = async (params = {}) => {
    return apiService.GET('/api/admin/registrations/export', params, {
        responseType: 'blob'
    });
};

/**
 * 取得單筆報名詳情
 * @param {number} submissionId - 報名 ID (submission_id)
 * @returns {Promise} API 響應
 */
export const getRegistrationDetail = async (submissionId) => {
    return apiService.GET(`/api/admin/registrations/${submissionId}`);
};

// ========== 報名取消申請 (Form Cancellation Requests) ==========

/**
 * 取得取消申請列表
 * @param {Object} params - status / form_activity_id / keyword / page / per_page
 */
export const getFormCancellationRequests = async (params = {}) => {
    return apiService.GET('/api/admin/form-cancellation-requests', params);
};

/**
 * 取得單筆取消申請詳情
 * @param {number} id
 */
export const getFormCancellationRequest = async (id) => {
    return apiService.GET(`/api/admin/form-cancellation-requests/${id}`);
};

/**
 * 通過取消申請
 * @param {number} id
 * @param {Object} data - { review_note }
 */
export const approveFormCancellationRequest = async (id, data = {}) => {
    return apiService.POST(
        `/api/admin/form-cancellation-requests/${id}/approve`,
        data
    );
};

/**
 * 拒絕取消申請
 * @param {number} id
 * @param {Object} data - { review_note }
 */
export const rejectFormCancellationRequest = async (id, data = {}) => {
    return apiService.POST(
        `/api/admin/form-cancellation-requests/${id}/reject`,
        data
    );
};

/**
 * 取得取消申請附件下載 URL（5 分鐘有效期）
 * @param {number} id
 */
export const getFormCancellationAttachment = async (id) => {
    return apiService.GET(
        `/api/admin/form-cancellation-requests/${id}/attachment`
    );
};

// ========== 學院修業進路 (Course Paths) ==========

export const getCoursePathCapabilityOptions = async () => {
    return apiService.GET('/api/admin/course-paths/capability-options');
};

export const getCoursePaths = async (params = {}) => {
    return apiService.GET('/api/admin/course-paths', params);
};

export const getCoursePath = async (id) => {
    return apiService.GET(`/api/admin/course-paths/${id}`);
};

export const createCoursePath = async (data) => {
    return apiService.POST('/api/admin/course-paths', data);
};

export const updateCoursePath = async (id, data) => {
    return apiService.PUT(`/api/admin/course-paths/${id}`, data);
};

export const toggleCoursePath = async (id) => {
    return apiService.PATCH(`/api/admin/course-paths/${id}/toggle`);
};

export const deleteCoursePath = async (id) => {
    return apiService.DELETE(`/api/admin/course-paths/${id}`);
};
